export const DEFAULT_LANGUAGE = "en";
export const LANGUAGE_STORAGE_KEY = "appLanguage";

const LANG_CODES = [
  "en",
  "hi",
  "mr",
  "gu",
  "ta",
  "te",
  "kn",
  "ml",
  "bn",
  "pa",
  "ur",
];

/** Populated by initI18n() from /i18n/translations/*.json (public folder). */
export const translations = {};

const translationsUrl = (lang) =>
  `${process.env.PUBLIC_URL || ""}/i18n/translations/${lang}.json`;

/**
 * Loads all locale JSON from public/i18n/translations. Call once before first render
 * of translated UI (e.g. from App startup).
 */
export const initI18n = async () => {
  await Promise.all(
    LANG_CODES.map(async (lang) => {
      const res = await fetch(translationsUrl(lang));
      if (!res.ok) {
        throw new Error(`i18n: failed to load ${translationsUrl(lang)} (${res.status})`);
      }
      translations[lang] = await res.json();
    })
  );
  window.dispatchEvent(new CustomEvent("app-translations-loaded"));
};

export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "hi", label: "हिन्दी" },
  { value: "mr", label: "मराठी" },
  { value: "gu", label: "ગુજરાતી" },
  { value: "ta", label: "தமிழ்" },
  { value: "te", label: "తెలుగు" },
  { value: "kn", label: "ಕನ್ನಡ" },
  { value: "ml", label: "മലയാളം" },
  { value: "bn", label: "বাংলা" },
  { value: "pa", label: "ਪੰਜਾਬੀ" },
  { value: "ur", label: "اردو" },
];

export const getCurrentLanguage = () => {
  const selected = localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE;
  return translations[selected] ? selected : DEFAULT_LANGUAGE;
};

export const setCurrentLanguage = (language) => {
  const normalized = translations[language] ? language : DEFAULT_LANGUAGE;
  localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  window.dispatchEvent(
    new CustomEvent("app-language-change", { detail: { language: normalized } })
  );
};

export const t = (key, language = getCurrentLanguage()) => {
  const selectedMap = translations[language] || translations[DEFAULT_LANGUAGE] || {};
  const defaultMap = translations[DEFAULT_LANGUAGE] || {};
  return selectedMap[key] || defaultMap[key] || key;
};

const COLUMN_KEY_MAP = {
  Name: "name",
  Watchlist: "watchlist",
  "Current Price": "currentPrice",
  "Current Value": "currentValueCol",
  "Net Change": "netChange",
  "Net Quantity": "netQuantity",
  "Previous Close": "previousClose",
  "Buy Value": "buyValue",
  "Buy Quantity": "buyQuantity",
  "Avg Buy Price": "avgBuyPrice",
  "Sell Quantity": "sellQuantity",
  "Sell Value": "sellValue",
  "Avg Sell Price": "averageSellPrice",
  "Profit/Loss": "profitLoss",
  TodaysHigh: "todaysHigh",
  TodaysLow: "todaysLow",
  "Initial Price": "initialPrice",
  "Target Price": "targetPrice",
  "Target %": "targetPercent",
  Notification: "notification",
  Days: "days",
  DaysLeft: "daysLeft",
  "Order Id": "orderId",
  PQty: "pendingTotalQty",
  Type: "type",
  Quantity: "quantity",
  "Order Status": "orderStatus",
  Reason: "reason",
  Date: "date",
  Price: "price",
  Exch: "exchange",
  Actions: "actions",
};

const COLUMN_LABELS = {
  en: {
    name: "Name",
    watchlist: "Watchlist",
    currentPrice: "Current Price",
    currentValueCol: "Current Value",
    netChange: "Net Change",
    netQuantity: "Net Quantity",
    previousClose: "Previous Close",
    buyValue: "Buy Value",
    buyQuantity: "Buy Quantity",
    avgBuyPrice: "Avg Buy Price",
    sellQuantity: "Sell Quantity",
    sellValue: "Sell Value",
    averageSellPrice: "Average Sell Price",
    profitLoss: "Profit/Loss",
    todaysHigh: "Today's High",
    todaysLow: "Today's Low",
    initialPrice: "Initial Price",
    targetPrice: "Target Price",
    targetPercent: "Target %",
    notification: "Notification",
    days: "Days",
    daysLeft: "Days Left",
    orderId: "Order Id",
    pendingTotalQty: "PQty",
    type: "Type",
    quantity: "Quantity",
    orderStatus: "Order Status",
    reason: "Reason",
    date: "Date",
    price: "Price",
    exchange: "Exchange",
    actions: "Actions",
  },
  hi: {
    name: "नाम",
    watchlist: "वॉचलिस्ट",
    currentPrice: "वर्तमान मूल्य",
    currentValueCol: "वर्तमान मूल्य",
    netChange: "शुद्ध परिवर्तन",
    netQuantity: "शुद्ध मात्रा",
    previousClose: "पिछला क्लोज",
    buyValue: "खरीद मूल्य",
    buyQuantity: "खरीद मात्रा",
    avgBuyPrice: "औसत खरीद मूल्य",
    sellQuantity: "बिक्री मात्रा",
    sellValue: "बिक्री मूल्य",
    averageSellPrice: "औसत बिक्री मूल्य",
    profitLoss: "लाभ/हानि",
    todaysHigh: "आज का उच्च",
    todaysLow: "आज का निम्न",
    initialPrice: "प्रारंभिक मूल्य",
    targetPrice: "लक्ष्य मूल्य",
    targetPercent: "लक्ष्य %",
    notification: "सूचना",
    days: "दिन",
    daysLeft: "शेष दिन",
    orderId: "ऑर्डर आईडी",
    pendingTotalQty: "लंबित/कुल मात्रा",
    type: "प्रकार",
    quantity: "मात्रा",
    orderStatus: "ऑर्डर स्थिति",
    reason: "कारण",
    date: "तारीख",
    price: "मूल्य",
    exchange: "एक्सचेंज",
    actions: "कार्रवाइयाँ",
  },
  mr: {
    name: "नाव",
    watchlist: "वॉचलिस्ट",
    currentPrice: "सध्याची किंमत",
    currentValueCol: "सध्याचे मूल्य",
    netChange: "निव्वळ बदल",
    netQuantity: "निव्वळ प्रमाण",
    previousClose: "मागील क्लोज",
    buyValue: "खरेदी मूल्य",
    buyQuantity: "खरेदी प्रमाण",
    avgBuyPrice: "सरासरी खरेदी किंमत",
    sellQuantity: "विक्री प्रमाण",
    sellValue: "विक्री मूल्य",
    averageSellPrice: "सरासरी विक्री किंमत",
    profitLoss: "नफा/तोटा",
    todaysHigh: "आजचा उच्च",
    todaysLow: "आजचा कमी",
    initialPrice: "प्रारंभिक किंमत",
    targetPrice: "लक्ष्य किंमत",
    targetPercent: "लक्ष्य %",
    notification: "सूचना",
    days: "दिवस",
    daysLeft: "शिल्लक दिवस",
    orderId: "ऑर्डर आयडी",
    pendingTotalQty: "प्रलंबित/एकूण प्रमाण",
    type: "प्रकार",
    quantity: "प्रमाण",
    orderStatus: "ऑर्डर स्थिती",
    reason: "कारण",
    date: "तारीख",
    price: "किंमत",
    exchange: "एक्स्चेंज",
    actions: "कृती",
  },
  gu: {
    name: "નામ",
    watchlist: "વૉચલિસ્ટ",
    currentPrice: "વર્તમાન ભાવ",
    currentValueCol: "વર્તમાન મૂલ્ય",
    netChange: "નેટ બદલાવ",
    netQuantity: "નેટ જથ્થો",
    previousClose: "પાછલો ક્લોઝ",
    buyValue: "ખરીદી મૂલ્ય",
    buyQuantity: "ખરીદી જથ્થો",
    avgBuyPrice: "સરેરાશ ખરીદી ભાવ",
    sellQuantity: "વેચાણ જથ્થો",
    sellValue: "વેચાણ મૂલ્ય",
    averageSellPrice: "સરેરાશ વેચાણ ભાવ",
    profitLoss: "નફો/નુકસાન",
    todaysHigh: "આજનું ઊંચું",
    todaysLow: "આજનું નીચું",
    initialPrice: "શરૂઆતનો ભાવ",
    targetPrice: "ટાર્ગેટ ભાવ",
    targetPercent: "ટાર્ગેટ %",
    notification: "સૂચના",
    days: "દિવસ",
    daysLeft: "બાકી દિવસ",
    orderId: "ઓર્ડર આઈડી",
    pendingTotalQty: "બાકી/કુલ જથ્થો",
    type: "પ્રકાર",
    quantity: "જથ્થો",
    orderStatus: "ઓર્ડર સ્થિતિ",
    reason: "કારણ",
    date: "તારીખ",
    price: "ભાવ",
    exchange: "એક્સચેન્જ",
    actions: "ક્રિયાઓ",
  },
  ta: {
    name: "பெயர்",
    watchlist: "வாட்ச்லிஸ்ட்",
    currentPrice: "தற்போதைய விலை",
    currentValueCol: "தற்போதைய மதிப்பு",
    netChange: "நெட் மாற்றம்",
    netQuantity: "நெட் அளவு",
    previousClose: "முந்தைய க்ளோஸ்",
    buyValue: "வாங்கு மதிப்பு",
    buyQuantity: "வாங்கு அளவு",
    avgBuyPrice: "சராசரி வாங்கு விலை",
    sellQuantity: "விற்பனை அளவு",
    sellValue: "விற்பனை மதிப்பு",
    averageSellPrice: "சராசரி விற்பனை விலை",
    profitLoss: "லாபம்/இழப்பு",
    todaysHigh: "இன்றைய உயர்",
    todaysLow: "இன்றைய தாழ்",
    initialPrice: "ஆரம்ப விலை",
    targetPrice: "இலக்கு விலை",
    targetPercent: "இலக்கு %",
    notification: "அறிவிப்பு",
    days: "நாட்கள்",
    daysLeft: "மீதமுள்ள நாட்கள்",
    orderId: "ஆர்டர் ஐடி",
    pendingTotalQty: "நிலுவை/மொத்த அளவு",
    type: "வகை",
    quantity: "அளவு",
    orderStatus: "ஆர்டர் நிலை",
    reason: "காரணம்",
    date: "தேதி",
    price: "விலை",
    exchange: "எக்சேஞ்ச்",
    actions: "செயல்கள்",
  },
  te: {
    name: "పేరు",
    watchlist: "వాచ్‌లిస్ట్",
    currentPrice: "ప్రస్తుత ధర",
    currentValueCol: "ప్రస్తుత విలువ",
    netChange: "నెట్ మార్పు",
    netQuantity: "నెట్ పరిమాణం",
    previousClose: "మునుపటి క్లోజ్",
    buyValue: "కొనుగోలు విలువ",
    buyQuantity: "కొనుగోలు పరిమాణం",
    avgBuyPrice: "సగటు కొనుగోలు ధర",
    sellQuantity: "అమ్మకపు పరిమాణం",
    sellValue: "అమ్మకపు విలువ",
    averageSellPrice: "సగటు అమ్మకపు ధర",
    profitLoss: "లాభం/నష్టం",
    todaysHigh: "ఈ రోజు గరిష్టం",
    todaysLow: "ఈ రోజు కనిష్టం",
    initialPrice: "ప్రారంభ ధర",
    targetPrice: "లక్ష్య ధర",
    targetPercent: "లక్ష్యం %",
    notification: "నోటిఫికేషన్",
    days: "రోజులు",
    daysLeft: "మిగిలిన రోజులు",
    orderId: "ఆర్డర్ ఐడి",
    pendingTotalQty: "పెండింగ్/మొత్తం పరిమాణం",
    type: "రకం",
    quantity: "పరిమాణం",
    orderStatus: "ఆర్డర్ స్థితి",
    reason: "కారణం",
    date: "తేదీ",
    price: "ధర",
    exchange: "ఎక్స్చేంజ్",
    actions: "చర్యలు",
  },
  kn: {
    name: "ಹೆಸರು",
    watchlist: "ವಾಚ್‌ಲಿಸ್ಟ್",
    currentPrice: "ಪ್ರಸ್ತುತ ಬೆಲೆ",
    currentValueCol: "ಪ್ರಸ್ತುತ ಮೌಲ್ಯ",
    netChange: "ನೆಟ್ ಬದಲಾವಣೆ",
    netQuantity: "ನೆಟ್ ಪ್ರಮಾಣ",
    previousClose: "ಹಿಂದಿನ ಕ್ಲೋಸ್",
    buyValue: "ಖರೀದಿ ಮೌಲ್ಯ",
    buyQuantity: "ಖರೀದಿ ಪ್ರಮಾಣ",
    avgBuyPrice: "ಸರಾಸರಿ ಖರೀದಿ ಬೆಲೆ",
    sellQuantity: "ಮಾರಾಟ ಪ್ರಮಾಣ",
    sellValue: "ಮಾರಾಟ ಮೌಲ್ಯ",
    averageSellPrice: "ಸರಾಸರಿ ಮಾರಾಟ ಬೆಲೆ",
    profitLoss: "ಲಾಭ/ನಷ್ಟ",
    todaysHigh: "ಇಂದಿನ ಹೆಚ್ಚು",
    todaysLow: "ಇಂದಿನ ಕಡಿಮೆ",
    initialPrice: "ಆರಂಭಿಕ ಬೆಲೆ",
    targetPrice: "ಲಕ್ಷ್ಯ ಬೆಲೆ",
    targetPercent: "ಲಕ್ಷ್ಯ %",
    notification: "ಅಧಿಸೂಚನೆ",
    days: "ದಿನಗಳು",
    daysLeft: "ಉಳಿದ ದಿನಗಳು",
    orderId: "ಆರ್ಡರ್ ಐಡಿ",
    pendingTotalQty: "ಬಾಕಿ/ಒಟ್ಟು ಪ್ರಮಾಣ",
    type: "ಪ್ರಕಾರ",
    quantity: "ಪ್ರಮಾಣ",
    orderStatus: "ಆರ್ಡರ್ ಸ್ಥಿತಿ",
    reason: "ಕಾರಣ",
    date: "ದಿನಾಂಕ",
    price: "ಬೆಲೆ",
    exchange: "ಎಕ್ಸ್‌ಚೇಂಜ್",
    actions: "ಕ್ರಿಯೆಗಳು",
  },
  ml: {
    name: "പേര്",
    watchlist: "വാച്ച്‌ലിസ്റ്റ്",
    currentPrice: "നിലവിലെ വില",
    currentValueCol: "നിലവിലെ മൂല്യം",
    netChange: "നെറ്റ് മാറ്റം",
    netQuantity: "നെറ്റ് അളവ്",
    previousClose: "മുൻ ക്ലോസ്",
    buyValue: "വാങ്ങൽ മൂല്യം",
    buyQuantity: "വാങ്ങൽ അളവ്",
    avgBuyPrice: "ശരാശരി വാങ്ങൽ വില",
    sellQuantity: "വിൽപ്പന അളവ്",
    sellValue: "വിൽപ്പന മൂല്യം",
    averageSellPrice: "ശരാശരി വിൽപ്പന വില",
    profitLoss: "ലാഭം/നഷ്ടം",
    todaysHigh: "ഇന്നത്തെ ഉയരം",
    todaysLow: "ഇന്നത്തെ താഴ്‌ച",
    initialPrice: "ആരംഭ വില",
    targetPrice: "ലക്ഷ്യ വില",
    targetPercent: "ലക്ഷ്യം %",
    notification: "അറിയിപ്പ്",
    days: "ദിവസങ്ങൾ",
    daysLeft: "ശേഷിക്കുന്ന ദിവസങ്ങൾ",
    orderId: "ഓർഡർ ഐഡി",
    pendingTotalQty: "പെൻഡിംഗ്/ആകെ അളവ്",
    type: "തരം",
    quantity: "അളവ്",
    orderStatus: "ഓർഡർ നില",
    reason: "കാരണം",
    date: "തീയതി",
    price: "വില",
    exchange: "എക്സ്ചേഞ്ച്",
    actions: "പ്രവർത്തനങ്ങൾ",
  },
  bn: {
    name: "নাম",
    watchlist: "ওয়াচলিস্ট",
    currentPrice: "বর্তমান দাম",
    currentValueCol: "বর্তমান মূল্য",
    netChange: "নেট পরিবর্তন",
    netQuantity: "নেট পরিমাণ",
    previousClose: "পূর্ববর্তী ক্লোজ",
    buyValue: "ক্রয় মূল্য",
    buyQuantity: "ক্রয় পরিমাণ",
    avgBuyPrice: "গড় ক্রয় মূল্য",
    sellQuantity: "বিক্রয় পরিমাণ",
    sellValue: "বিক্রয় মূল্য",
    averageSellPrice: "গড় বিক্রয় মূল্য",
    profitLoss: "লাভ/ক্ষতি",
    todaysHigh: "আজকের উচ্চ",
    todaysLow: "আজকের নিম্ন",
    initialPrice: "প্রারম্ভিক মূল্য",
    targetPrice: "লক্ষ্য মূল্য",
    targetPercent: "লক্ষ্য %",
    notification: "বিজ্ঞপ্তি",
    days: "দিন",
    daysLeft: "বাকি দিন",
    orderId: "অর্ডার আইডি",
    pendingTotalQty: "মুলতুবি/মোট পরিমাণ",
    type: "ধরন",
    quantity: "পরিমাণ",
    orderStatus: "অর্ডার স্ট্যাটাস",
    reason: "কারণ",
    date: "তারিখ",
    price: "দাম",
    exchange: "এক্সচেঞ্জ",
    actions: "অ্যাকশন",
  },
  pa: {
    name: "ਨਾਂ",
    watchlist: "ਵਾਚਲਿਸਟ",
    currentPrice: "ਮੌਜੂਦਾ ਕੀਮਤ",
    currentValueCol: "ਮੌਜੂਦਾ ਮੁੱਲ",
    netChange: "ਨੈੱਟ ਬਦਲਾਅ",
    netQuantity: "ਨੈੱਟ ਮਾਤਰਾ",
    previousClose: "ਪਿਛਲਾ ਕਲੋਜ਼",
    buyValue: "ਖਰੀਦ ਮੁੱਲ",
    buyQuantity: "ਖਰੀਦ ਮਾਤਰਾ",
    avgBuyPrice: "ਔਸਤ ਖਰੀਦ ਕੀਮਤ",
    sellQuantity: "ਵਿਕਰੀ ਮਾਤਰਾ",
    sellValue: "ਵਿਕਰੀ ਮੁੱਲ",
    averageSellPrice: "ਔਸਤ ਵਿਕਰੀ ਕੀਮਤ",
    profitLoss: "ਮੁਨਾਫਾ/ਨੁਕਸਾਨ",
    todaysHigh: "ਅੱਜ ਦਾ ਉੱਚਾ",
    todaysLow: "ਅੱਜ ਦਾ ਨੀਵਾਂ",
    initialPrice: "ਸ਼ੁਰੂਆਤੀ ਕੀਮਤ",
    targetPrice: "ਟਾਰਗੇਟ ਕੀਮਤ",
    targetPercent: "ਟਾਰگੇਟ %",
    notification: "ਸੂਚਨਾ",
    days: "ਦਿਨ",
    daysLeft: "ਬਾਕੀ ਦਿਨ",
    orderId: "ਆਰਡਰ ਆਈਡੀ",
    pendingTotalQty: "ਲੰਬਿਤ/ਕੁੱਲ ਮਾਤਰਾ",
    type: "ਕਿਸਮ",
    quantity: "ਮਾਤਰਾ",
    orderStatus: "ਆਰਡਰ ਸਥਿਤੀ",
    reason: "ਕਾਰਣ",
    date: "ਤਾਰੀਖ",
    price: "ਕੀਮਤ",
    exchange: "ਐਕਸਚੇਂਜ",
    actions: "ਕਾਰਵਾਈਆਂ",
  },
  ur: {
    name: "نام",
    watchlist: "واچ لسٹ",
    currentPrice: "موجودہ قیمت",
    currentValueCol: "موجودہ قدر",
    netChange: "خالص تبدیلی",
    netQuantity: "نیٹ مقدار",
    previousClose: "پچھلا کلوز",
    buyValue: "خرید قیمت",
    buyQuantity: "خرید مقدار",
    avgBuyPrice: "اوسط خرید قیمت",
    sellQuantity: "فروخت مقدار",
    sellValue: "فروخت قیمت",
    averageSellPrice: "اوسط فروخت قیمت",
    profitLoss: "منافع/نقصان",
    todaysHigh: "آج کی بلند",
    todaysLow: "آج کی کم",
    initialPrice: "ابتدائی قیمت",
    targetPrice: "ہدف قیمت",
    targetPercent: "ہدف %",
    notification: "اطلاع",
    days: "دن",
    daysLeft: "باقی دن",
    orderId: "آرڈر آئی ڈی",
    pendingTotalQty: "زیر التوا/کل مقدار",
    type: "قسم",
    quantity: "مقدار",
    orderStatus: "آرڈر کی حیثیت",
    reason: "وجہ",
    date: "تاریخ",
    price: "قیمت",
    exchange: "ایکسچینج",
    actions: "اعمال",
  },
};

export const translateColumnLabel = (column, language = getCurrentLanguage()) => {
  const key = COLUMN_KEY_MAP[column];
  if (!key) return column;
  return (
    (COLUMN_LABELS[language] && COLUMN_LABELS[language][key]) ||
    COLUMN_LABELS.en[key] ||
    column
  );
};
